import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';

/**
 * Resolves the full deal context (conversation + funnel + mapping + stages)
 * for the full-page Deal Record view, given a conversationId (route param)
 * and a funnelId ref (route query).
 *
 * Funnel list: mirrors `useFunnelManager`/`KanbanView` — loads via the
 * `funnels/get` Vuex action (cached 30s) and reads `funnels/getFunnels`.
 * There is no `KanbanAPI.getFunnels()`; `KanbanAPI` only exposes
 * per-funnel Kanban endpoints (stage_conversations, move_conversation, etc.)
 *
 * Conversation: mirrors `ConversationModal.vue`'s `loadConversation` — first
 * looks up the conversation already in the Vuex store (`getConversationById`,
 * populated e.g. by the Kanban list load, which eager-loads
 * `funnel_mappings`), and only dispatches `getConversation` (single-show
 * API) if it isn't there yet. Note: the `getConversation` action itself
 * does not return the conversation (it only commits it to the store), so we
 * re-read from the store getter after dispatching rather than using the
 * action's resolved value.
 */
export function useDealRecord(conversationId, funnelId) {
  const store = useStore();
  const isLoading = ref(true);
  const funnels = ref([]);
  const rawConversation = ref(null);

  const funnel = computed(
    () =>
      funnels.value.find(f => String(f.id) === String(funnelId?.value)) ||
      funnels.value[0] ||
      {}
  );

  const stages = computed(() => {
    const funnelStages = funnel.value?.stages || {};
    return Object.keys(funnelStages)
      .map(name => ({ name, ...funnelStages[name] }))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  });

  const conversation = computed(() => rawConversation.value || {});

  const mapping = computed(
    () =>
      (conversation.value.funnel_mappings || []).find(
        m => String(m.funnel_id) === String(funnel.value?.id)
      ) || {}
  );

  const load = async () => {
    const conversationIdInt = Number(conversationId);
    // Mostra na hora o que já existe no store (sem spinner), mas SEMPRE
    // revalida na API: a cópia do store pode estar defasada (ex.:
    // funnel_mappings de antes de um Ganhou/mover — a barra de etapas ficava
    // presa na etapa antiga). O show atualiza o store via UPDATE_CONVERSATION.
    const cached = store.getters.getConversationById(conversationIdInt);
    if (cached) rawConversation.value = cached;
    isLoading.value = !cached;
    try {
      await store.dispatch('funnels/get');
      funnels.value = store.getters['funnels/getFunnels'] || [];

      await store.dispatch('getConversation', conversationIdInt);
      rawConversation.value =
        store.getters.getConversationById(conversationIdInt) ||
        rawConversation.value;
    } finally {
      isLoading.value = false;
    }
  };

  return { conversation, mapping, funnel, stages, isLoading, load };
}
